import { PropsWithChildren, ReactNode } from 'react';
import { BookingHeader } from '../../Layouts/Booking/BookingHeader';
import { BookingWrapper, BookingWrapperWidth } from '../../Layouts/Booking/BookingWrapper';
import type { BookingFlowProgress } from './bookingFlowProgress';

/** Layout props a booking step screen forwards to this shell untouched. */
export type BookingFlowScreenLayoutProps = {
  title: string;
  /** Unset on screens outside the counted steps, e.g. the mobile-vs-stationary choice. */
  progress?: BookingFlowProgress;
  /** A node so part of it can be emphasized, e.g. the number a code went to. */
  description?: ReactNode;
  hideBackButton?: boolean;
  width?: BookingWrapperWidth;
  wrapperClassName?: string;
  hideBottomBar?: boolean;
  customBottomBar?: ReactNode;
};

type Props = BookingFlowScreenLayoutProps & {
  /** Rendered above the header inside the wrapper (e.g. mobile banners). */
  beforeHeader?: ReactNode;
  /** Used when `customBottomBar` is not provided and the bottom bar is shown. */
  defaultBottomBar?: ReactNode;
};

/**
 * Shared layout shell for mobile and stationary booking steps:
 * wrapper + header + optional bottom bar. Back sits in the bottom bar when
 * there is one, so the header carries it only on steps without a bar.
 */
export const BookingFlowScreen = ({
  title,
  progress,
  children,
  description,
  hideBackButton,
  width,
  wrapperClassName,
  hideBottomBar,
  customBottomBar,
  beforeHeader,
  defaultBottomBar
}: PropsWithChildren<Props>) => {
  const bottomBar = hideBottomBar ? undefined : (customBottomBar ?? defaultBottomBar);

  return (
    <>
      <BookingWrapper width={width} wrapperClassName={wrapperClassName}>
        {beforeHeader}
        <BookingHeader
          title={title}
          hideBackButton={hideBackButton || !!bottomBar}
          description={description}
          progress={progress}
        />
        {children}
      </BookingWrapper>
      {bottomBar}
    </>
  );
};
